import { useState, useMemo, useCallback } from 'react'

import {
  AnalyticsResponse,
  EventMetric,
  type Event,
} from '@tribeplatform/gql-client/types'
import { useEventAnalytics } from '@tribeplatform/react-sdk/hooks'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { VirtualList } from '@tribeplatform/react-ui-kit/VirtualList'

import { useI18n } from '../../../i18n/providers/I18nProvider.js'
import { cn } from '../../../utils/cn.js'
import { EventAnalyticsModal } from '../../EventAnalytics/EventAnalyticsModal.js'

import { EmptyList } from './EmptyList.js'
import { EventListHeader } from './EventListHeader.js'
import { EventListItem } from './EventListItem.js'
import { LoadingList } from './LoadingList.js'
import { useSearchEvents } from './useSearchEvents.js'

const ESTIMATED_ITEM_SIZE = 48

interface Props {
  limit?: number
  metric?: EventMetric
}

export const TopEventsList = ({ limit = 15, metric }: Props) => {
  const { $t } = useI18n()
  const [analyticsModal, setAnalyticsModal] = useState<Event>()
  const [offset, setOffset] = useState(0)
  const {
    setSearchQuery,
    debouncedSearchQuery,
    searchedEventIds,
    isSearchLoading,
  } = useSearchEvents()
  console.log('searchedEventIds', searchedEventIds)

  const handleLoadMore = useCallback(() => {
    setOffset(prev => prev + limit)
  }, [limit])

  const itemComparator = useCallback(
    (itemA: RecordDetails['event'], itemB: RecordDetails['event']) => {
      return itemA?.id === itemB?.id
    },
    [],
  )

  const renderItem = useCallback(
    ({ event, value }: RecordDetails) => (
      <EventListItem
        title={event?.title}
        value={value}
        onClick={() => setAnalyticsModal(event)}
      />
    ),
    [setAnalyticsModal],
  )

  const title = useMemo(() => {
    switch (metric) {
      case EventMetric.uniqueVisitors:
        return $t({
          id: 'Analytics.TopEventsList.Title.UniqueVisitors',
          defaultMessage: 'Top events by: Unique visitors',
        })
      case EventMetric.uniqueRegistrants:
        return $t({
          id: 'Analytics.TopEventsList.Title.TotalRegistrants',
          defaultMessage: 'Top events by: Total registrants',
        })
      case EventMetric.conversionRatio:
        return $t({
          id: 'Analytics.TopEventsList.Title.Ratio',
          defaultMessage: 'Top events by: Conversion rate',
        })
      default: {
        const _exhaustiveCheck: never = metric
        return _exhaustiveCheck
      }
    }
  }, [$t, metric])

  const { data, isLoading, isFetching, isInitialLoading } = useEventAnalytics({
    fields: {
      entities: {
        event: 'basic',
      },
    },
    variables: {
      ...(searchedEventIds.length
        ? { eventRecurrenceIds: searchedEventIds }
        : {}),
      metric,
      limit: offset + limit + 1, // Fetch all items up to current offset + one extra to check if there's more
      offset: 0, // Always fetch from beginning to accumulate items
    },
    useQueryOptions: {
      keepPreviousData: true,
    },
  })

  const { displayItems, hasMore } = useMemo(() => {
    if (!data) {
      return { displayItems: [], hasMore: false }
    }

    const allItems = getEntitiesAndValues(data, metric)
    // We fetch offset + limit + 1 items, so if we got more than offset + limit, there's more to load
    const hasMore = allItems.length > offset + limit
    const displayItems = hasMore ? allItems.slice(0, offset + limit) : allItems
    return { displayItems, hasMore }
  }, [data, metric, offset, limit])

  const getItemKey = useCallback(
    (index: number) => {
      const item = displayItems[index]
      return item?.event?.id ?? index
    },
    [displayItems],
  )

  const renderEmpty =
    displayItems.length === 0 && !isSearchLoading && !isLoading
  const isLoadingOrHasError = isLoading || !data
  const renderList = !isLoadingOrHasError && !isInitialLoading

  return (
    <>
      <Card
        className={cn(
          'p-4 justify-start',
          isInitialLoading && 'animate-pulse bg-surface-pressed',
          isLoadingOrHasError && !isInitialLoading && 'opacity-70',
        )}
      >
        <EventListHeader
          title={title}
          onSearch={setSearchQuery}
          searchPlaceholder={$t({
            id: 'Analytics.TopEventsList.SearchPlaceholder',
            defaultMessage: 'Search events',
          })}
        />
        <div className="flex-1">
          <div className="max-h-120 h-120 flex-1 relative mt-4">
            {renderEmpty && <EmptyList hasQuery={!!debouncedSearchQuery} />}
            {renderList && (
              <VirtualList
                items={displayItems}
                getItemKey={getItemKey}
                itemRenderer={renderItem}
                itemComparator={itemComparator}
                estimateSize={() => ESTIMATED_ITEM_SIZE}
                onTailReached={() => {
                  if (hasMore && !isLoading && !isFetching) {
                    handleLoadMore()
                  }
                }}
              />
            )}
            {isLoadingOrHasError && <LoadingList />}
          </div>
        </div>
      </Card>
      {analyticsModal && (
        <EventAnalyticsModal
          open
          onClose={() => setAnalyticsModal(null)}
          event={analyticsModal}
        />
      )}
    </>
  )
}

const getValueField = (metric?: EventMetric) => {
  switch (metric) {
    case EventMetric.uniqueVisitors:
      return 'visitor_count'
    case EventMetric.uniqueRegistrants:
      return 'registrant_count'
    case EventMetric.conversionRatio:
      return 'conversion_ratio'
    default: {
      const _exhaustiveCheck: never = metric
      return _exhaustiveCheck
    }
  }
}

interface RecordDetails {
  value: number | string
  event?: Event
}

const getEntitiesAndValues = (
  response: AnalyticsResponse,
  metric?: EventMetric,
): RecordDetails[] => {
  const entitiesAndValues = response.records.map(item => {
    const metricField = getValueField(metric)
    const value = item?.payload?.find(p => p.key === metricField)?.value
    const event = item?.entities?.event

    return { value: value ?? 0, event }
  })

  return entitiesAndValues
}
